import React, { useState, useEffect } from "react";
import { Select, Checkbox, Tag } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { getPharmacyItems } from "../../apis/pharmacyItems";
import {
  saveProductBundle,
  updateProductBundle,
} from "../../apis/productBundles";
import { PharmacyItem } from "../../interfaces/PharmacyItems";
import { ProductBundleItem } from "../../interfaces/ProductBundle";
import { ProductBundle } from "../../interfaces/ProductBundle";

const { Option } = Select;

const normalizeBundleItem = (item: any, index: number) => ({
  pharmacyItemId: item.pharmacy_item_id ?? item.pharmacyItemId ?? 0,
  quantity: item.quantity ?? 1,
  sortOrder: item.sort_order ?? item.sortOrder ?? index,
  tempId: `row-${item.pharmacy_item_id ?? item.pharmacyItemId ?? index}-${index}`,
});

const hasUsableBatches = (item: PharmacyItem): boolean => {
  const raw = item?.batches || [];
  if (raw.length === 0) return false;
  const usable = raw
    .map((batch: any) => ({
      available_quantity: batch.availableQuantity ?? batch.available_quantity ?? 0,
      is_active: batch.isActive ?? batch.is_active ?? true,
      is_expired: batch.isExpired ?? batch.is_expired ?? false,
    }))
    .filter(
      (b: any) =>
        b.available_quantity > 0 && b.is_active && !b.is_expired
    );
  return usable.length > 0;
};

type AddProductBundleProps = {
  closeModal: () => void;
  onSuccess?: () => void;
  editableBundle?: ProductBundle | null;
};

const AddProductBundle: React.FC<AddProductBundleProps> = ({
  closeModal,
  onSuccess,
  editableBundle,
}) => {
  const isEditMode = Boolean(editableBundle?.id);
  const [pharmacyItems, setPharmacyItems] = useState<PharmacyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isActive: true,
    notes: "" as string | null,
    items: [] as (ProductBundleItem & { tempId?: string })[],
  });

  const getClinicId = (): number => {
    try {
      const user = JSON.parse(window.localStorage.getItem("user") || "{}");
      return user && user.activeClinic ? user.activeClinic.id : 1;
    } catch {
      return 1;
    }
  };

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res: any = await getPharmacyItems({
          per_page: 1000,
          is_active: true,
        });
        const data = Array.isArray(res) ? res : res?.data || [];
        setPharmacyItems(data);
      } catch (err) {
        console.error("Failed to fetch pharmacy items:", err);
      }
    };
    fetchItems();
  }, []);

  useEffect(() => {
    if (editableBundle) {
      const active =
        editableBundle.isActive ?? editableBundle.is_active ?? true;
      const rawItems = editableBundle.items ?? [];
      setFormData({
        name: editableBundle.name ?? "",
        description: editableBundle.description ?? "",
        isActive: active,
        notes: editableBundle.notes ?? "",
        items: rawItems.map((item: any, index: number) =>
          normalizeBundleItem(item, index),
        ),
      });
    } else {
      setFormData({
        name: "",
        description: "",
        isActive: true,
        notes: "",
        items: [],
      });
    }
  }, [editableBundle]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const [multiselectValue, setMultiselectValue] = useState<number[]>([]);

  const addOrMergeItems = (pharmacyIds: number[]) => {
    if (!pharmacyIds.length) return;
    setFormData((prev) => {
      const next = [...prev.items];
      pharmacyIds.forEach((id) => {
        const existingIndex = next.findIndex((i) => i.pharmacyItemId === id);
        if (existingIndex >= 0) {
          next[existingIndex] = {
            ...next[existingIndex],
            quantity: (next[existingIndex].quantity ?? 1) + 1,
          };
        } else {
          next.push({
            pharmacyItemId: id,
            quantity: 1,
            sortOrder: next.length,
            tempId: `row-${id}-${Date.now()}`,
          });
        }
      });
      return { ...prev, items: next };
    });
    setMultiselectValue([]);
  };

  const removeItemRow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, sortOrder: i })),
    }));
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    const qty = Math.max(1, quantity);
    setFormData((prev) => {
      const next = [...prev.items];
      next[index] = { ...next[index], quantity: qty };
      return { ...prev, items: next };
    });
  };

  const validateForm = (): boolean => {
    const e: { [key: string]: string } = {};
    if (!formData.name?.trim()) e.name = "Name is required";
    if (!formData.items.length) e.items = "Add at least one pharmacy item";
    const invalidItems = formData.items.filter(
      (i) => !i.pharmacyItemId || i.pharmacyItemId === 0,
    );
    if (invalidItems.length) e.items = "Select a pharmacy item for each row";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        clinicId: getClinicId(),
        isActive: formData.isActive,
        notes: formData.notes?.trim() || null,
        items: formData.items
          .filter((i) => i.pharmacyItemId > 0)
          .map((item, index) => ({
            pharmacyItemId: item.pharmacyItemId,
            quantity: item.quantity ?? 1,
            sortOrder: item.sortOrder ?? index,
          })),
      };
      if (isEditMode && editableBundle?.id) {
        await updateProductBundle(editableBundle.id, payload);
      } else {
        await saveProductBundle(payload);
      }
      onSuccess?.();
      closeModal();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to save bundle";
      setErrors((prev) => ({ ...prev, submit: msg }));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6";

  return (
    <div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className={inputClass}
          />
          {errors.name && (
            <span className="text-sm text-red-500">{errors.name}</span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-900">
              Description
            </label>
            <textarea
              rows={2}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900">
              Notes
            </label>
            <textarea
              rows={2}
              value={formData.notes ?? ""}
              onChange={(e) => handleChange("notes", e.target.value || null)}
              className={inputClass}
            />
          </div>
        </div>
        <div className="flex items-center">
          <Checkbox
            checked={formData.isActive}
            onChange={(e) => handleChange("isActive", e.target.checked)}
          >
            Active
          </Checkbox>
        </div>

        {/* Bundle items: multiselect + selected list */}
        <div className="p-4 rounded-lg border border-gray-200 bg-gray-50/50">
          <label className="block mb-2 text-sm font-medium text-gray-900">
            Pharmacy Items <span className="text-red-500">*</span>
          </label>
          <Select
            mode="multiple"
            placeholder="Search and select pharmacy items to add..."
            value={multiselectValue}
            onChange={(ids: number[]) => addOrMergeItems(ids)}
            style={{ width: "100%" }}
            showSearch
            filterOption={(input, option: any) => {
              const item = pharmacyItems.find((p) => p.id === option?.value);
              if (!item) return false;
              const q = input.toLowerCase();
              return (
                (item.name || "").toLowerCase().includes(q) ||
                (item.sku || "").toLowerCase().includes(q)
              );
            }}
            optionLabelProp="label"
          >
            {pharmacyItems.map((item) => (
              <Option
                key={item.id}
                value={item.id}
                label={item.name}
                disabled={!hasUsableBatches(item)}
              >
                <span className="flex flex-wrap gap-2 items-center">
                  <span>
                    {item.name}
                    {item.sku && ` - ${item.sku}`}
                    {item.form && ` (${item.form})`}
                  </span>
                  {!hasUsableBatches(item) && (
                    <Tag color="red">Not available</Tag>
                  )}
                </span>
              </Option>
            ))}
          </Select>
          {errors.items && (
            <p className="mt-1 text-sm text-red-500">{errors.items}</p>
          )}

          {formData.items.length === 0 ? (
            <p className="py-4 mt-3 text-sm text-center text-gray-500 rounded-md border border-gray-200 border-dashed bg-white/50">
              No items added yet. Select items from the dropdown above.
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-gray-600">
                Selected items ({formData.items.length})
              </p>
              <div className="overflow-y-auto space-y-2 max-h-56">
                {formData.items.map((row, index) => {
                  const pharmacyItem = pharmacyItems.find(
                    (p) => p.id === row.pharmacyItemId,
                  );
                  const displayName =
                    pharmacyItem?.name ?? `Item #${row.pharmacyItemId}`;
                  return (
                    <div
                      key={row.tempId ?? `${row.pharmacyItemId}-${index}`}
                      className="flex gap-3 items-center p-2 bg-white rounded-md border border-gray-200 shadow-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">
                          {displayName}
                        </span>
                        {pharmacyItem?.sku && (
                          <span className="text-xs text-gray-500">
                            {pharmacyItem.sku}
                            {pharmacyItem.form && ` • ${pharmacyItem.form}`}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 items-center shrink-0">
                        <label className="text-xs text-gray-500 whitespace-nowrap">
                          Qty
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={row.quantity ?? 1}
                          onChange={(e) =>
                            updateItemQuantity(
                              index,
                              parseInt(e.target.value, 10) || 1,
                            )
                          }
                          className="px-2 py-1 w-16 text-sm text-center rounded border border-gray-300"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItemRow(index)}
                        className="p-1 text-red-600 rounded hover:text-red-800 hover:bg-red-50"
                        title="Remove"
                      >
                        <DeleteOutlined className="text-base" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {errors.submit && (
          <p className="text-sm text-red-500">{errors.submit}</p>
        )}
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onSave}
          type="button"
          disabled={loading}
          className="px-3 py-2 text-white rounded-md bg-primary hover:bg-hover disabled:opacity-50"
        >
          {loading
            ? isEditMode
              ? "Updating..."
              : "Saving..."
            : isEditMode
              ? "Update "
              : "Save "}
        </button>
        <button
          onClick={closeModal}
          type="button"
          className="text-sm font-semibold leading-6 text-gray-900"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddProductBundle;
